import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n/I18nContext";

/**
 * UrgencyBanner — landing-page conversion strip anchored to the villa's
 * summer campaign window (15 August → 30 September). Shows live availability
 * inside that window, the longest bookable stretch, and a deep-linked
 * Reserve CTA that pre-fills the /booking date picker.
 *
 * Renders nothing while loading or if the API errors, so it never breaks the
 * layout.
 */
export default function UrgencyBanner({ windowStart, windowEnd, hookStart, hookEnd } = {}) {
  const { t, lang } = useI18n();
  const [data, setData] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const params = {};
    if (windowStart) params.window_start = windowStart;
    if (windowEnd) params.window_end = windowEnd;
    if (hookStart) params.hook_start = hookStart;
    if (hookEnd) params.hook_end = hookEnd;
    api.get("/availability/urgency", { params })
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoaded(true));
  }, [windowStart, windowEnd, hookStart, hookEnd]);

  if (!loaded || !data || data.in_past) {
    return null;
  }
  // Prefer the headline hook (e.g., "August 2026") but fall back to the
  // wider search window if the hook has no future nights.
  const hook = data.hook || {};
  const hookNights = hook.available_nights ?? data.available_nights;
  const hookLabel = translateHookLabel(hook.label || data.window_label, lang);
  const range = data.next_range;

  if (hookNights === 0 && !range) return null;

  // Deep-link to /booking with the longest bookable window pre-selected.
  const reserveHref = range
    ? `/booking?checkin=${range.checkin}&checkout=${range.checkout}`
    : "/booking";

  const currencyGlyph =
    (range?.currency || data.currency || "EUR").toUpperCase() === "EUR"
      ? "€"
      : (range?.currency || "EUR").toUpperCase();

  return (
    <section
      className="bg-ionian text-cream border-t border-b border-ionian/20"
      data-testid="urgency-banner"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-8 md:py-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-10 items-center">
          {/* Left — label + count */}
          <div className="md:col-span-5">
            <div className="text-[10px] tracking-[0.32em] uppercase text-cream/60">
              {t("urgency.overline")}
            </div>
            <div className="mt-3 font-sans text-2xl md:text-3xl font-extralight leading-tight">
              <span
                data-testid="urgency-nights"
                className="font-serif tabular-nums text-sand"
              >
                {hookNights}
              </span>{" "}
              {hookNights === 1 ? t("urgency.night_singular") : t("urgency.night_plural")}{" "}
              {t("urgency.remaining_in")}{" "}
              <span data-testid="urgency-hook-label" className="font-serif text-sand">
                {hookLabel}
              </span>
              .
            </div>
          </div>

          {/* Middle — best window */}
          {range && (
            <div className="md:col-span-4 md:border-l md:border-cream/15 md:pl-8">
              <div className="text-[10px] tracking-[0.32em] uppercase text-cream/60">
                {t("urgency.best_window")}
              </div>
              <div
                data-testid="urgency-window"
                className="mt-3 font-serif text-lg md:text-xl leading-tight text-sand"
              >
                {formatRange(range.checkin, range.checkout, lang)} ·{" "}
                <span className="text-cream/85">
                  {range.nights} {range.nights === 1 ? t("urgency.night_singular") : t("urgency.night_plural")}
                </span>
              </div>
              {range.avg_nightly_rate != null && (
                <div className="mt-1 text-sm text-cream/75 font-light tabular-nums">
                  {t("urgency.from")} {currencyGlyph}
                  {Math.round(range.avg_nightly_rate).toLocaleString()} / {t("urgency.night_singular")}
                </div>
              )}
            </div>
          )}

          {/* Right — CTA */}
          <div className={range ? "md:col-span-3 md:text-right" : "md:col-span-7 md:text-right"}>
            <Link
              to={reserveHref}
              data-testid="urgency-reserve-btn"
              className="inline-flex items-center gap-2 px-6 py-3 bg-sand text-ionian hover:bg-cream transition-colors font-sans text-sm tracking-[0.18em] uppercase font-medium"
            >
              {t("urgency.cta")}
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <path d="M5 12h14M13 5l7 7-7 7"/>
              </svg>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

// -----------------------------------------------------------------------------
// helpers

function translateHookLabel(label, lang) {
  if (!label) return "";
  if (lang !== "gr") return label;
  const map = {
    January: "Ιανουάριος", February: "Φεβρουάριος", March: "Μάρτιος",
    April: "Απρίλιος", May: "Μάιος", June: "Ιούνιος",
    July: "Ιούλιος", August: "Αύγουστος", September: "Σεπτέμβριος",
    October: "Οκτώβριος", November: "Νοέμβριος", December: "Δεκέμβριος",
  };
  // Handle "August 2026" full-month labels
  const parts = label.split(" ");
  if (parts.length === 2 && map[parts[0]]) return `${map[parts[0]]} ${parts[1]}`;
  return label;
}

function translateRangeLabel(startISO, endISO, lang) {
  const s = new Date(startISO + "T12:00:00");
  const e = new Date(endISO + "T12:00:00");
  const locale = lang === "gr" ? "el-GR" : "en-GB";
  const fmt = (d) => d.toLocaleDateString(locale, { day: "numeric", month: "short" });
  const yr = e.getFullYear();
  return `${fmt(s)} → ${fmt(e)} ${yr}`;
}

function formatRange(ciISO, coISO, lang) {
  const ci = new Date(ciISO + "T12:00:00");
  const co = new Date(coISO + "T12:00:00");
  const locale = lang === "gr" ? "el-GR" : "en-GB";
  const fmt = (d) => d.toLocaleDateString(locale, { day: "numeric", month: "short" });
  return `${fmt(ci)} → ${fmt(co)}`;
}
